import { useAuthStore } from '@/stores/auth';

// API client with JWT authentication support
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

class ApiClient {
  private baseURL: string;
  // Laufendes Refresh-Promise — verhindert parallele Refresh-Versuche
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Get JWT token from auth store
   */
  private getAuthToken(): string | null {
    try {
      const authStore = useAuthStore();
      const token = authStore.token;
      
      console.log('[API] Auth Store Status:', {
        hasToken: !!token,
        tokenLength: token?.length || 0,
        isAuthenticated: authStore.isAuthenticated,
        user: authStore.user?.email || 'no user'
      });
      
      return token;
    } catch (error) {
      console.warn('Could not get auth token:', error);
      return null;
    }
  }

  /**
   * Build headers with authentication
   */
  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    const token = this.getAuthToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  async get(endpoint: string) {
    return this.makeRequest('GET', endpoint);
  }

  async post(endpoint: string, data: any) {
    return this.makeRequest('POST', endpoint, data);
  }

  async put(endpoint: string, data: any) {
    return this.makeRequest('PUT', endpoint, data);
  }

  async delete(endpoint: string) {
    return this.makeRequest('DELETE', endpoint);
  }

  /**
   * Zentrale Methode für alle API-Anfragen mit automatischem Token-Refresh
   */
  private async makeRequest(method: string, endpoint: string, data?: any, isRetry = false): Promise<any> {
    try {
      console.log(`[API] ${method} ${this.baseURL}${endpoint}`, data || '');
      
      // Headers müssen bei jedem Request neu generiert werden (wichtig nach Token-Refresh)
      const requestOptions: RequestInit = {
        method,
        headers: this.getHeaders(), // Wird bei jedem Request neu aufgerufen
      };

      if (data && method !== 'GET') {
        requestOptions.body = JSON.stringify(data);
      }

      const response = await fetch(`${this.baseURL}${endpoint}`, requestOptions);

      // Bei 401: Token-Refresh versuchen — alle parallelen Requests teilen sich
      // dasselbe Refresh-Promise (verhindert mehrfache Refresh-Aufrufe und Toast-Spam)
      if (response.status === 401 && !isRetry) {
        const { useAuthStore } = await import('../stores/auth');
        const authStore = useAuthStore();

        if (!this.refreshPromise) {
          this.refreshPromise = authStore.refreshAccessToken().finally(() => {
            this.refreshPromise = null;
          });
        }

        const refreshSuccess = await this.refreshPromise;

        if (refreshSuccess) {
          return this.makeRequest(method, endpoint, data, true);
        } else {
          await authStore.handleTokenExpiry();
          return new Promise(() => {});
        }
      }

      // 401 nach Retry → Refresh hat definitiv nicht geholfen
      if (response.status === 401 && isRetry) {
        const { useAuthStore } = await import('../stores/auth');
        const authStore = useAuthStore();
        await authStore.handleTokenExpiry();
        return new Promise(() => {});
      }

      // Response-Body auslesen (auch bei Fehlern), um die Backend-Fehlermeldung
      // (JsonResponse::error message) statt eines generischen "HTTP 400: Bad Request"
      // anzuzeigen. Body kann bei Nicht-JSON-Antworten (z.B. HTML-Fehlerseite) fehlschlagen.
      let responseData: any = null;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        try {
          responseData = await response.json();
        } catch {
          responseData = null;
        }
      }

      if (!response.ok) {
        const message = responseData?.message || `HTTP ${response.status}: ${response.statusText}`;
        throw new Error(message);
      }

      console.log(`[API] ${method} Response:`, responseData);
      return responseData;
    } catch (error) {
      console.error(`API ${method} Error:`, error);
      throw error;
    }
  }
}

const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;
